
import { ParserError } from '@angular/compiler';
import 'antlr4';
import antlr4 from 'antlr4';
import RuleNode from 'antlr4/tree/RuleNode';
import { Lazy, lazy } from 'src/app/shared/utils';
import { default as InsightsQueryLexer } from './parsing/InsightsQueryLexer';
import { default as InsightsQueryParser } from './parsing/InsightsQueryParser';
import { default as InsightsQueryVisitor } from './parsing/InsightsQueryVisitor';
import { StringEscape } from '../app/shared/utils/stringEscape';
import { logger } from '../app/pbir/utils/logger';
import { StringUtils } from '../app/pbir/utils/StringUtils';

const LOGGER = logger('InsightsQuery');

export namespace InsightsQuery {
  export function parse(expression: string, errors?: ParseError[], throwOnError?: boolean) {
    try {
      let inputStream = new antlr4.InputStream(expression);
      let lexer = new InsightsQueryLexer(inputStream);
      let tokens = new antlr4.CommonTokenStream(lexer);
      let parser = new InsightsQueryParser(tokens);

      parser.buildParseTrees = true;

      if (errors) {
        let errorListener = new ErrorListener(errors);

        lexer.removeErrorListeners();
        lexer.addErrorListener(errorListener);
        parser.removeErrorListeners();
        parser.addErrorListener(errorListener);
      }

      let tree = parser.rootExpression();

      return <InsightsQuerySyntax>(tree as any)?.accept(new AntlrParseTreeVisitor(expression));
    }
    catch (err) {
      if (throwOnError !== false)
        throw err;

      LOGGER.error(err);
    }
  }

  class ErrorListener extends antlr4.error.ErrorListener {
    constructor(private errors: ParseError[]) {
      super();
    }

    syntaxError(
      recognizer: any,
      offendingSymbol: antlr4.Token,
      line: number,
      column: number,
      msg: string,
      e: antlr4.error.RecognitionException,
    ) {
      let size = offendingSymbol ? (offendingSymbol.stop - offendingSymbol.start + 1) : 1;
      let endColumn = column + size;

      if (msg.indexOf("missing ')'") !== -1) {
        this.errors.push(
          new ParseError(
            new TextLocation(line, column, line, endColumn), `Expected a closing parenthesis.`));
      }
      else if (msg.indexOf("missing ']'") !== -1) {
        this.errors.push(
          new ParseError(
            new TextLocation(line, column, line, endColumn), `Expected a closing bracket.`));
      }
      else {
        if (e instanceof antlr4.error.InputMismatchException) {
          let message = offendingSymbol.type === antlr4.Token.EOF
            ? `Unexpected end of expression.`
            : `Unexpected syntax '${offendingSymbol.text}'`;

          this.errors.push(
            new ParseError(
              new TextLocation(line, column, line, endColumn), message));
        }
        else if ((msg.indexOf("missing '") !== -1)
          || (msg.indexOf("mismatched input") !== -1)
          || (msg.indexOf("extraneous input") !== -1)
          || (msg.indexOf("token recognition error") !== -1)) {
          this.errors.push(
            new ParseError(
              new TextLocation(line, column, line, endColumn), offendingSymbol ? `Unexpected syntax '${offendingSymbol.text}'.` : `Unexpected syntax.`));
        }
      }
    }
  }

  export class TextLocation {
    static readonly first = new TextLocation(1, 1, 1, 1);

    constructor(
      public startLine: number,
      public startColumn: number,
      public endLine: number,
      public endColumn: number) { }

    get startLineIndex() { return this.startLine - 1; }
    get startColumnIndex() { return this.startColumn - 1; }
    get endLineIndex() { return this.endLine - 1; }
    get endColumnIndex() { return this.endColumn - 1; }

    get singleline() { return this.startLine === this.endLine; }

    equals(other: TextLocation) {
      return this.startLine == other.startLine
        && this.startColumn == other.startColumn
        && this.endLine == other.endLine
        && this.endColumn == other.endColumn;
    }

    toString() {
      if (this.startLine == this.endLine) {
        if (this.startColumn == this.endColumn) {
          return `${this.startLine}:${this.startColumn}`;
        }
        else {
          return `${this.startLine}:${this.startColumn}-${this.endColumn}`;
        }
      }
      else {
        return `${this.startLine}:${this.startColumn}-${this.endLine}:${this.endColumn}`;
      }
    }
  }

  export class SyntaxSpan extends TextLocation {
    constructor(
      private readonly _sourceLines: Lazy<string[]>,
      public startLine: number,
      public startColumn: number,
      public endLine: number,
      public endColumn: number) {
      super(startLine, startColumn, endLine, endColumn);
    }

    get sourceLines() { return this._sourceLines.value; }

    toString() {
      let sourceLines = this.sourceLines;

      if (this.singleline)
        return sourceLines[this.startLineIndex].substring(this.startColumnIndex, this.endColumnIndex);

      let buffer: string[] = [];

      buffer.push(sourceLines[this.startLineIndex].substring(this.startColumnIndex));

      for (let l = this.startLineIndex + 1; l < this.endLineIndex; ++l) {
        buffer.push(sourceLines[l]);
      }

      buffer.push(sourceLines[this.endLineIndex].substring(this.startColumnIndex, this.endColumnIndex));

      return buffer.join('\n');
    }

    get debugString() {
      return this.toString();
    }
  }

  export class ParseError extends Error {
    constructor(public readonly location: TextLocation, message: string) {
      super(message);
    }
  }

  class AntlrParseTreeVisitor extends InsightsQueryVisitor {
    constructor(private readonly expression: string) {
      super();
    }

    private readonly sourceLines = lazy(() => this.expression.split('\n'));


    private createSpan(ctx: antlr4.ParserRuleContext): SyntaxSpan;
    private createSpan(start: antlr4.Token, stop: antlr4.Token): SyntaxSpan;
    private createSpan(startOrCtx: antlr4.Token | antlr4.ParserRuleContext, stop?: antlr4.Token): SyntaxSpan {
      let start: antlr4.Token;

      if (stop === undefined) {
        start = (startOrCtx.start === -1) ? null : <antlr4.Token>startOrCtx.start;
        stop = (startOrCtx.stop === -1) ? null : <antlr4.Token>startOrCtx.stop;
      }
      else {
        start = <antlr4.Token>startOrCtx;
      }

      let startLine = start.line;
      let startColumn = start.column + 1;

      let endLine = startLine;
      let endColumn = startColumn;

      if (stop) {
        endLine = stop.line;
        endColumn = stop.column + 1;
      }
      else
        stop = start;

      endColumn += stop.stop - stop.start;

      return new SyntaxSpan(this.sourceLines, startLine, startColumn, endLine, endColumn);
    }

    visitRootExpression(ctx: InsightsQueryParser.RootExpressionContext) {
      return ctx.expression().accept(this);
    }

    visitNumberLiteral(ctx: InsightsQueryParser.NumberLiteralContext) {
      let text = <string>(ctx.NUMBER() as any).getText();

      let value = Number(text);

      if (isNaN(value))
        throw new ParseError(this.createSpan(ctx), `Invalid number`);

      return new InsightsQuery.LiteralSyntax(this.createSpan(ctx), value);
    }

    visitStringLiteral(ctx: InsightsQueryParser.StringLiteralContext) {
      let value = ctx.getText();

      let escapedText = ctx.getText().substring(1, value.length - 1);

      let text = StringEscape.unescape(escapedText);

      return new InsightsQuery.LiteralSyntax(this.createSpan(ctx), text);
    }

    visitParenthesizedExpr(ctx: InsightsQueryParser.ParenthesizedExprContext) {
      return ctx.expression().accept(this);
    }

    visitUnaryPlusExpr(ctx: InsightsQueryParser.UnaryPlusExprContext) {
      return new InsightsQuery.UnarySyntax(
        this.createSpan(ctx),
        InsightsQuery.UnaryOperator.Plus,
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitUnaryMinusExpr(ctx: InsightsQueryParser.UnaryMinusExprContext) {
      return new InsightsQuery.UnarySyntax(
        this.createSpan(ctx),
        InsightsQuery.UnaryOperator.Minus,
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitUnaryNotExpr(ctx: InsightsQueryParser.UnaryNotExprContext) {
      return new InsightsQuery.UnarySyntax(
        this.createSpan(ctx),
        InsightsQuery.UnaryOperator.Not,
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitRaiseToExpr(ctx: InsightsQueryParser.RaiseToExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.RaiseTo,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitDivideExpr(ctx: InsightsQueryParser.DivideExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Divide,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitAddExpr(ctx: InsightsQueryParser.AddExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Add,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitSubtractExpr(ctx: InsightsQueryParser.SubtractExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Subtract,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitMultiplyExpr(ctx: InsightsQueryParser.MultiplyExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Multiply,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitLessThanExpr(ctx: InsightsQueryParser.LessThanExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.LessThan,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitGreaterThanExpr(ctx: InsightsQueryParser.GreaterThanExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.GreaterThan,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitLessThanOrEqualExpr(ctx: InsightsQueryParser.LessThanOrEqualExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.LessThanOrEqual,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitGreaterThanOrEqualExpr(ctx: InsightsQueryParser.GreaterThanOrEqualExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.GreaterThanOrEqual,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitEqualExpr(ctx: InsightsQueryParser.EqualExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Equal,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitNotEqualExpr(ctx: InsightsQueryParser.NotEqualExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.NotEqual,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitConcatExpr(ctx: InsightsQueryParser.ConcatExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Concat,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitAndExpr(ctx: InsightsQueryParser.AndExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.And,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitOrExpr(ctx: InsightsQueryParser.OrExprContext) {
      return new InsightsQuery.BinarySyntax(
        this.createSpan(ctx),
        InsightsQuery.BinaryOperator.Or,
        <InsightsQuerySyntax>ctx.Left.accept(this),
        <InsightsQuerySyntax>ctx.Right.accept(this)
      );
    }

    visitFunctionCall(ctx: InsightsQueryParser.FunctionCallContext) {
      let functionName = <string>(ctx.UNESCAPED_IDENTIFIER() as any).getText();

      if (!ctx.LPAREN()) {
        throw new ParseError(this.createSpan(ctx), `Expected an opening parenthesis following ${functionName}.`);
      }

      if (!ctx.RPAREN()) {
        throw new ParseError(this.createSpan(ctx), `Expected a closing parenthesis.`);
      }

      let args: InsightsQuerySyntax[] = [];

      let callArgs = ctx.callArgs()?.values();
      if (callArgs) {
        args = (callArgs.expression as any)().map(a => this.visit(a));
      }

      return new InsightsQuery.FunctionCallSyntax(this.createSpan(ctx), functionName, args);
    }

    visitIdentifier(ctx: InsightsQueryParser.IdentifierContext) {
      let identifier = <string>(ctx.ESCAPED_IDENTIFIER() as any).getText();

      let value = identifier.substring(1, identifier.length - 1).trim();
      
      let split = StringUtils.split(value, '.', 3, true);

      let table: string;
      let field: string;
      let childField: string;

      if (split.length == 1) {
        table = null;
        field = split[0];
      }
      else {
        table = split[0];
        field = split[1];

        if (split.length == 3) {
          childField = split[2];
        } else {
          childField = null;
        }
      }

      return new IdentifierSyntax(this.createSpan(ctx), table, field, childField);
    }


    visitFunctionCallExpr(ctx: InsightsQueryParser.FunctionCallExprContext) {
      return ctx.functionCall().accept(this);
    }

    visitIdentifierExpr(ctx: InsightsQueryParser.IdentifierExprContext) {
      return ctx.identifier().accept(this);
    }

    visitLiteralExpr(ctx: InsightsQueryParser.LiteralContext) {
      return this.visitChildren(ctx);
    }


    visit(ctx: any): any {
      let result = super.visit(ctx);

      if (Array.isArray(result)) {
        result = result[0];
      }

      return result;
    }

    visitChildren(ctx: any): any {
      return super.visitChildren(ctx);
    }

    visitErrorNode(ctx: any): any {
      return null;
    }

    visitTerminal(ctx: any): any {
      return null;
    }
  }
}

export abstract class InsightsQuerySyntaxVisitor<R = void, P = undefined> {
  visit(syntax: InsightsQuerySyntax, param?: P): R {
    if (!syntax) {
      throw new Error(`null syntax`);
    }

    if (syntax instanceof InsightsQuery.LiteralSyntax)
      return this.visitLiteral(syntax, param);

    if (syntax instanceof InsightsQuery.UnarySyntax)
      return this.visitUnary(syntax, param);

    if (syntax instanceof InsightsQuery.BinarySyntax)
      return this.visitBinary(syntax, param);

    if (syntax instanceof InsightsQuery.FunctionCallSyntax)
      return this.visitFunctionCall(syntax, param);

    if (syntax instanceof InsightsQuery.IdentifierSyntax)
      return this.visitIdentifier(syntax, param);

    throw new Error(`syntax is not of a known InsightsQuerySyntax`);
  }

  visitLiteral(syntax: InsightsQuery.LiteralSyntax, param?: P): R {
    return;
  }

  visitUnary(syntax: InsightsQuery.UnarySyntax, param?: P): R {
    if (syntax.right)
      this.visit(syntax.right, param);

    return;
  }

  visitBinary(syntax: InsightsQuery.BinarySyntax, param?: P): R {
    if (syntax.left)
      this.visit(syntax.left, param);

    if (syntax.right)
      this.visit(syntax.right, param);

    return;
  }

  visitFunctionCall(syntax: InsightsQuery.FunctionCallSyntax, param?: P): R {
    for (let arg of syntax.args) {
      if (arg)
        this.visit(arg, param);
    }

    return;
  }

  visitIdentifier(syntax: InsightsQuery.IdentifierSyntax, param?: P): R {
    return;
  }
}

class ToStringInsightsQuerySyntaxVisitor extends InsightsQuerySyntaxVisitor {
  constructor(
    public buffer: string[],
    public preferOperators: boolean) {
    super();
  }

  visitBinary(syntax: InsightsQuery.BinarySyntax) {
    this.visit(syntax.left);

    this.buffer.push(' ');

    switch (syntax.operator) {
      case InsightsQuery.BinaryOperator.RaiseTo:
        this.buffer.push('^');
        break;

      case InsightsQuery.BinaryOperator.Multiply:
        this.buffer.push('*');
        break;

      case InsightsQuery.BinaryOperator.Divide:
        this.buffer.push('/');
        break;

      case InsightsQuery.BinaryOperator.Add:
        this.buffer.push('+');
        break;

      case InsightsQuery.BinaryOperator.Subtract:
        this.buffer.push('-');
        break;

      case InsightsQuery.BinaryOperator.GreaterThan:
        this.buffer.push('>');
        break;

      case InsightsQuery.BinaryOperator.GreaterThanOrEqual:
        this.buffer.push('>=');
        break;

      case InsightsQuery.BinaryOperator.LessThan:
        this.buffer.push('<');
        break;

      case InsightsQuery.BinaryOperator.LessThanOrEqual:
        this.buffer.push('<=');
        break;

      case InsightsQuery.BinaryOperator.NotEqual:
        this.buffer.push('<>');
        break;

      case InsightsQuery.BinaryOperator.Equal:
        this.buffer.push('==');
        break;

      case InsightsQuery.BinaryOperator.Concat:
        this.buffer.push('&');
        break;

      case InsightsQuery.BinaryOperator.And:
        this.buffer.push(this.preferOperators ? '&&' : 'AND');
        break;

      case InsightsQuery.BinaryOperator.Or:
        this.buffer.push(this.preferOperators ? '||' : 'OR');
        break;
    }

    this.buffer.push(' ');

    this.visit(syntax.right);
  }

  visitFunctionCall(syntax: InsightsQuery.FunctionCallSyntax) {
    this.buffer.push(syntax.functionName);
    this.buffer.push('(');

    let lastIndex = syntax.args.length - 1;

    for (let i = 0; i < syntax.args.length; ++i) {
      this.visit(syntax.args[i]);

      if (i < lastIndex)
        this.buffer.push(', ');
    }

    this.buffer.push(')');
  }

  visitIdentifier(syntax: InsightsQuery.IdentifierSyntax) {
    this.buffer.push('[');

    if (syntax.table) {
      this.buffer.push(syntax.table);
      this.buffer.push('.');
    }

    this.buffer.push(syntax.field);

    if (syntax.childField) {
      this.buffer.push('.');
      this.buffer.push(syntax.childField);
    }

    this.buffer.push(']');
  }

  visitLiteral(syntax: InsightsQuery.LiteralSyntax) {
    if (syntax.value === null || syntax.value === undefined) {
      this.buffer.push('NULL()');
    }
    else if (syntax.value === true) {
      this.buffer.push('TRUE()');
    }
    else if (syntax.value === false) {
      this.buffer.push('FALSE()');
    }
    else if (typeof syntax.value === 'string') {
      this.buffer.push('"', syntax.value.replace(/\"/g, '""'), '"');
    }
    else if (typeof syntax.value === 'object' && 'toString' in syntax.value) {
      this.buffer.push('"', syntax.value.toString(), '"');
    }
    else {
      this.buffer.push(syntax.value);
    }
  }

  visitUnary(syntax: InsightsQuery.UnarySyntax) {
    switch (syntax.operator) {
      case InsightsQuery.UnaryOperator.Plus:
        this.buffer.push('+');
        break;

      case InsightsQuery.UnaryOperator.Minus:
        this.buffer.push('-');
        break;

      case InsightsQuery.UnaryOperator.Not:
        this.buffer.push(this.preferOperators ? '!' : 'NOT ');
        break;
    }

    this.visit(syntax.right);
  }
}

export enum InsightsQuerySyntaxKind {
  Literal,
  Unary,
  Binary,
  FunctionCall,
  Identifier
}

export abstract class InsightsQuerySyntax {
  constructor(
    public readonly kind: InsightsQuerySyntaxKind,
    public readonly span: InsightsQuery.SyntaxSpan) {
  }

  get location(): InsightsQuery.TextLocation { return this.span; }

  equals(other: InsightsQuerySyntax): boolean {
    return this.location == other.location && this.sameSyntax(other);
  }

  sameSyntax(other: InsightsQuerySyntax): boolean {
    return this.kind == other.kind;
  }

  toString(preferOperators = false, throwOnError = true) {
    try {
      let buffer: string[] = [];

      new ToStringInsightsQuerySyntaxVisitor(buffer, preferOperators).visit(this);

      return buffer.join('');
    }
    catch (err) {
      if (throwOnError)
        throw err;

      return null;
    }
  }

  get debugString() {
    return this.toString();
  }
}

export namespace InsightsQuery {
  export class LiteralSyntax extends InsightsQuerySyntax {
    constructor(
      span: InsightsQuery.SyntaxSpan,
      public readonly value: any) {
      super(InsightsQuerySyntaxKind.Literal, span);
    }

    sameSyntax(other: LiteralSyntax): boolean {
      return super.sameSyntax(other)
        && this.value == other.value;
    }
  }

  export enum UnaryOperator {
    Plus,
    Minus,
    Not
  }

  export class UnarySyntax extends InsightsQuerySyntax {
    constructor(
      span: InsightsQuery.SyntaxSpan,
      public readonly operator: UnaryOperator,
      public readonly right: InsightsQuerySyntax) {
      super(InsightsQuerySyntaxKind.Unary, span);
    }

    sameSyntax(other: UnarySyntax): boolean {
      return super.sameSyntax(other)
        && this.operator == other.operator
        && this.right.sameSyntax(other.right);
    }
  }

  export enum BinaryOperator {
    RaiseTo,
    Multiply,
    Divide,
    Add,
    Subtract,
    LessThan,
    GreaterThan,
    LessThanOrEqual,
    GreaterThanOrEqual,
    Equal,
    NotEqual,
    Concat,
    And,
    Or
  }

  export class BinarySyntax extends InsightsQuerySyntax {
    constructor(
      span: InsightsQuery.SyntaxSpan,
      public readonly operator: BinaryOperator,
      public readonly left: InsightsQuerySyntax,
      public readonly right: InsightsQuerySyntax) {
      super(InsightsQuerySyntaxKind.Binary, span);
    }

    sameSyntax(other: BinarySyntax): boolean {
      return super.sameSyntax(other)
        && this.operator == other.operator
        && this.left.sameSyntax(other.left)
        && this.right.sameSyntax(other.right);
    }
  }

  export class FunctionCallSyntax extends InsightsQuerySyntax {
    constructor(
      span: InsightsQuery.SyntaxSpan,
      public readonly functionName: string,
      public readonly args: InsightsQuerySyntax[]) {
      super(InsightsQuerySyntaxKind.FunctionCall, span);
    }

    sameSyntax(other: FunctionCallSyntax): boolean {
      return super.sameSyntax(other)
        && this.functionName == other.functionName
        && !this.args.find((a, i) => !a.sameSyntax(other.args[i]));
    }
  }

  export class IdentifierSyntax extends InsightsQuerySyntax {
    constructor(
      span: InsightsQuery.SyntaxSpan,
      public readonly table: string,
      public readonly field: string,
      public readonly childField: string) {
      super(InsightsQuerySyntaxKind.Identifier, span);
    }

    sameSyntax(other: IdentifierSyntax): boolean {
      return super.sameSyntax(other)
        && this.table == other.table
        && this.field == other.field
        && this.childField == other.childField;
    }
  }
}
